import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { JustificativaTemplate, PdcaConteudo } from './models';
import workflowService from './workflowService';

const FASES: { key: 'plan' | 'do' | 'check' | 'act'; label: string }[] = [
  { key: 'plan', label: 'PLAN' }, { key: 'do', label: 'DO' }, { key: 'check', label: 'CHECK' }, { key: 'act', label: 'ACT' },
];
const PILARES: { key: 'pessoas' | 'processos' | 'tecnologia'; label: string; icon: string }[] = [
  { key: 'pessoas', label: 'Pessoas', icon: '👤' }, { key: 'processos', label: 'Processos', icon: '⚙️' }, { key: 'tecnologia', label: 'Tecnologia', icon: '💻' },
];

const PDCA_VAZIO: PdcaConteudo = {
  plan: { pessoas: '', processos: '', tecnologia: '' },
  do: { pessoas: '', processos: '', tecnologia: '' },
  check: { pessoas: '', processos: '', tecnologia: '' },
  act: { pessoas: '', processos: '', tecnologia: '' },
};

const CadastroJustificativasPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ fromOnboarding?: boolean; etapa?: number }>();
  const fromOnboarding = location.state?.fromOnboarding ?? false;
  const onboardingEtapa = location.state?.etapa ?? 4;

  const [templates, setTemplates] = useState<JustificativaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');

  const [editando, setEditando] = useState<JustificativaTemplate | null>(null); // null = fechado; {templateId:0,...} = novo
  const [titulo, setTitulo] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [pdca, setPdca] = useState<PdcaConteudo>(PDCA_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState<number | null>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const carregar = () => {
    setLoading(true);
    workflowService.listarJustificativaTemplates().then(setTemplates).catch(() => setTemplates([])).finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, []);

  const abrirNovo = () => {
    setEditando({ templateId: 0, titulo: '', justificativaDetalhada: '', pdca: PDCA_VAZIO });
    setTitulo('');
    setJustificativa('');
    setPdca(PDCA_VAZIO);
  };

  const abrirEdicao = (t: JustificativaTemplate) => {
    setEditando(t);
    setTitulo(t.titulo);
    setJustificativa(t.justificativaDetalhada);
    setPdca(t.pdca);
  };

  const alterarCampo = (fase: 'plan' | 'do' | 'check' | 'act', pilar: 'pessoas' | 'processos' | 'tecnologia', valor: string) => {
    setPdca(prev => ({ ...prev, [fase]: { ...prev[fase], [pilar]: valor } }));
  };

  const salvar = async () => {
    if (!editando || !titulo.trim()) return;
    setSalvando(true);
    try {
      if (editando.templateId === 0) {
        await workflowService.criarJustificativaTemplate({ titulo: titulo.trim(), justificativaDetalhada: justificativa.trim(), pdca });
        showToast('Nova opção criada. Log SOX registrado.');
      } else {
        await workflowService.atualizarJustificativaTemplate(editando.templateId, { titulo: titulo.trim(), justificativaDetalhada: justificativa.trim(), pdca });
        showToast('Opção atualizada. Log SOX registrado.');
      }
      setEditando(null);
      carregar();
    } catch {
      showToast('Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (t: JustificativaTemplate) => {
    if (!window.confirm(`Excluir a opção "${t.titulo}"? Isso não afeta planos de melhoria já criados a partir dela.`)) return;
    setExcluindo(t.templateId);
    try {
      await workflowService.excluirJustificativaTemplate(t.templateId);
      showToast('Opção excluída. Log SOX registrado.');
      carregar();
    } catch (err: any) {
      showToast(err?.response?.data ?? 'Erro ao excluir — pode já estar em uso em algum Plano de Melhoria.');
    } finally {
      setExcluindo(null);
    }
  };

  return (
    <div className='wf-page'>
      <div className='wf-breadcrumb'>Home › Governança de TI › Catálogo de Justificativas PDCA</div>
      <div className='wf-header'>
        <div>
          <h1>Catálogo de Justificativas e Planos de Melhoria (PDCA)</h1>
          <p>Opções pré-cadastradas oferecidas ao concluir uma conta no Mapa de Conciliação. Edite as existentes ou crie novas.</p>
        </div>
        <div className='wf-header__actions'>
          <button className='wf-btn wf-btn--primary' onClick={abrirNovo}>+ Nova Opção</button>
          {fromOnboarding && (
            <button className='wf-btn wf-btn--secondary' onClick={() => history.push('/workflow-aprovacao/onboarding', { goToEtapa: onboardingEtapa })}>
              ← Onboarding Etapa {onboardingEtapa}
            </button>
          )}
          {!fromOnboarding && <button className='wf-btn wf-btn--secondary' onClick={() => history.goBack()}>Voltar</button>}
        </div>
      </div>

      {toast && <div className='wf-toast wf-toast--success'><strong>SOX</strong> {toast}</div>}

      {loading ? (
        <div className='wf-count' style={{ textAlign: 'center', padding: 40 }}>Carregando catálogo...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {templates.map(t => (
            <section className='wf-card' key={t.templateId}>
              <div className='wf-card__header'>
                <div>
                  <h2 style={{ fontSize: 15 }}>
                    {t.templateId} — {t.titulo}
                    {t.templateId <= 10 && <span className='wf-badge wf-badge--em_analise' style={{ marginLeft: 8, fontSize: 9 }}>padrão do sistema</span>}
                  </h2>
                  <p style={{ fontSize: 12 }}>{t.justificativaDetalhada || <em>Sem justificativa detalhada (preenchimento manual).</em>}</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className='wf-btn wf-btn--secondary wf-btn--sm' onClick={() => abrirEdicao(t)}>Editar</button>
                  <button className='wf-btn wf-btn--danger-outline wf-btn--sm' disabled={excluindo === t.templateId} onClick={() => excluir(t)}>
                    {excluindo === t.templateId ? 'Excluindo...' : 'Excluir'}
                  </button>
                </div>
              </div>
              {t.templateId !== 10 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                  {FASES.map(f => (
                    <div key={f.key} style={{ border: '1px solid #e8edf3', borderRadius: 8, padding: 10 }}>
                      <strong style={{ fontSize: 11 }}>{f.label}</strong>
                      {PILARES.map(p => (
                        <div key={p.key} style={{ fontSize: 10, color: '#5e6278', marginTop: 4 }}>
                          <strong>{p.icon} {p.label}:</strong> {t.pdca[f.key]?.[p.key] || '—'}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {editando && (
        <div className='wf-modal-bg'>
          <div className='wf-modal wf-modal--wide'>
            <div className='wf-modal__header'>
              <div>
                <h2>{editando.templateId === 0 ? 'Nova opção do catálogo' : `Editar opção ${editando.templateId}`}</h2>
                <p>Preenche automaticamente ao ser selecionada na submissão ao Workflow SOX — o usuário pode complementar depois.</p>
              </div>
              <button onClick={() => setEditando(null)}>×</button>
            </div>
            <div className='wf-modal__body'>
              <label>Título
                <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder='Ex: Divergência de Saldo Contábil' />
              </label>
              <label>Justificativa detalhada
                <textarea rows={3} value={justificativa} onChange={e => setJustificativa(e.target.value)} placeholder='Descreva a situação típica coberta por esta opção...' />
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14, marginTop: 6 }}>
                {FASES.map(f => (
                  <div key={f.key} style={{ border: '1px solid #e8edf3', borderRadius: 8, padding: 12 }}>
                    <strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>{f.label}</strong>
                    {PILARES.map(p => (
                      <label key={p.key} style={{ display: 'block', fontSize: 11, marginBottom: 8 }}>
                        {p.icon} {p.label}
                        <textarea
                          rows={2}
                          value={pdca[f.key][p.key]}
                          onChange={e => alterarCampo(f.key, p.key, e.target.value)}
                          style={{ width: '100%', marginTop: 4, fontSize: 11 }}
                        />
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>
            <div className='wf-modal__footer'>
              <button className='wf-btn wf-btn--secondary' onClick={() => setEditando(null)} disabled={salvando}>Cancelar</button>
              <button className='wf-btn wf-btn--primary' disabled={salvando || !titulo.trim()} onClick={salvar}>
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CadastroJustificativasPage;
